import React from 'react';
import { supabase } from '../config';

// GraphQL-like subscription service using Supabase real-time
class GraphQLSubscriptions {
  constructor() {
    this.subscriptions = new Map();
    this.channels = new Map();
  }

  // Subscribe to real-time updates
  async subscribe(subscriptionConfig, callback) {
    const {
      table,
      event = 'UPDATE', // INSERT, UPDATE, DELETE, or '*'
      filter = {},
      // select = '*', // Unused for now
      subscriptionId = null
    } = subscriptionConfig;

    const id = subscriptionId || this.generateSubscriptionId(table, event, filter);

    // Check if subscription already exists
    if (this.subscriptions.has(id)) {
      console.warn(`Subscription ${id} already exists`);
      return id;
    }

    try {
      // Create Supabase real-time channel
      const channelConfig = {
        event, 
        schema: 'public', 
        table
      };
      
      // Only add filter if it's not null/empty
      const builtFilter = this.buildFilter(filter);
      if (builtFilter) {
        channelConfig.filter = builtFilter;
      }
      
      const channel = supabase
        .channel(`subscription_${id}`)
        .on('postgres_changes', channelConfig, (payload) => {
          this.handleSubscriptionUpdate(id, payload, callback);
        })
        .subscribe((status) => {
          if (status === 'SUBSCRIBED') {
            console.log(`Subscription ${id} established`);
            this.subscriptions.set(id, {
              channel,
              config: subscriptionConfig,
              callback,
              status: 'active'
            });
          } else if (status === 'CHANNEL_ERROR') {
            console.error(`Subscription ${id} failed`);
            this.subscriptions.delete(id);
          }
        });

      this.channels.set(id, channel);
      return id;

    } catch (error) {
      console.error('Error creating subscription:', error);
      throw error;
    }
  }

  // Unsubscribe from updates
  unsubscribe(subscriptionId) {
    const subscription = this.subscriptions.get(subscriptionId);
    if (subscription) {
      const channel = this.channels.get(subscriptionId);
      if (channel) {
        supabase.removeChannel(channel);
        this.channels.delete(subscriptionId);
      }
      this.subscriptions.delete(subscriptionId);
      console.log(`Subscription ${subscriptionId} removed`);
    }
  }

  // Unsubscribe from all subscriptions
  unsubscribeAll() {
    for (const [id] of this.subscriptions) {
      this.unsubscribe(id);
    }
  }

  // Handle subscription updates
  handleSubscriptionUpdate(subscriptionId, payload, callback) {
    try {
      // Supabase realtime payload structure: { eventType: 'INSERT'|'UPDATE'|'DELETE', new: {...}, old: {...} }
      const updateData = {
        subscriptionId,
        eventType: payload.eventType || payload.event,
        table: payload.table || payload.schema,
        data: payload.new || payload.old,
        previousData: payload.old,
        timestamp: new Date().toISOString()
      };

      callback(updateData);
    } catch (error) {
      console.error('Error handling subscription update:', error);
    }
  }

  // Build filter for Supabase real-time
  buildFilter(filter) {
    if (!filter || Object.keys(filter).length === 0) {
      return null; // No filter means listen to all changes
    }
    return Object.entries(filter)
      .map(([key, value]) => `${key}=eq.${value}`)
      .join(',');
  }

  // Generate unique subscription ID
  generateSubscriptionId(table, event, filter) {
    const filterStr = JSON.stringify(filter);
    return `${table}_${event}_${btoa(filterStr).slice(0, 8)}`;
  }

  // Get subscription status
  getSubscriptionStatus(subscriptionId) {
    const subscription = this.subscriptions.get(subscriptionId);
    return subscription ? subscription.status : null;
  }

  // Get all active subscriptions
  getActiveSubscriptions() {
    return Array.from(this.subscriptions.keys());
  }

  // Subscription helpers for common use cases
  static createSubscriptionHelpers() {
    return {
      // Subscribe to equity record updates
      subscribeToEquityUpdates: (userId, callback) => {
        const subscriptions = new GraphQLSubscriptions();
        return subscriptions.subscribe({
          table: 'equity_records',
          event: '*',
          filter: { user_id: userId },
          subscriptionId: `equity_updates_${userId}`
        }, callback);
      },

      // Subscribe to dataroom document updates
      subscribeToDataroomUpdates: (projectId, callback) => {
        const subscriptions = new GraphQLSubscriptions();
        return subscriptions.subscribe({
          table: 'dataroom_items',
          event: '*',
          filter: { project_id: projectId },
          subscriptionId: `dataroom_updates_${projectId}`
        }, callback);
      },

      // Subscribe to user profile updates
      subscribeToUserUpdates: (userId, callback) => {
        const subscriptions = new GraphQLSubscriptions();
        return subscriptions.subscribe({
          table: 'user_profiles',
          event: 'UPDATE',
          filter: { user_id: userId },
          subscriptionId: `user_updates_${userId}`
        }, callback);
      },

      // Subscribe to notification updates
      subscribeToNotifications: (userId, callback) => {
        const subscriptions = new GraphQLSubscriptions();
        return subscriptions.subscribe({
          table: 'notifications',
          event: 'INSERT',
          filter: { user_id: userId },
          subscriptionId: `notifications_${userId}`
        }, callback);
      },

      // Subscribe to subscription plans updates
      subscribeToSubscriptionPlans: (callback) => {
        // Use the singleton instance for proper cleanup
        return graphQLSubscriptions.subscribe({
          table: 'subscription_plans',
          event: '*', // Listen to INSERT, UPDATE, DELETE
          filter: {}, // No filter - listen to all changes
          subscriptionId: 'subscription_plans_updates'
        }, callback);
      }
    };
  }
}

// Export singleton instance
export const graphQLSubscriptions = new GraphQLSubscriptions();

// Export subscription helpers
export const subscriptionHelpers = GraphQLSubscriptions.createSubscriptionHelpers();

// React hook for GraphQL subscriptions
export const useGraphQLSubscription = (subscriptionConfig, callback, deps = []) => {
  const [subscriptionId, setSubscriptionId] = React.useState(null);
  const [isConnected, setIsConnected] = React.useState(false);

  React.useEffect(() => {
    if (!subscriptionConfig) return;

    const subscribe = async () => {
      try {
        const id = await graphQLSubscriptions.subscribe(subscriptionConfig, (data) => {
          callback(data);
          setIsConnected(true);
        });
        setSubscriptionId(id);
      } catch (error) {
        console.error('Subscription error:', error);
        setIsConnected(false);
      }
    };

    subscribe();

    return () => {
      if (subscriptionId) {
        graphQLSubscriptions.unsubscribe(subscriptionId);
        setSubscriptionId(null);
        setIsConnected(false);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  return { subscriptionId, isConnected };
};

// Advanced subscription manager
export class SubscriptionManager {
  constructor() {
    this.subscriptions = new GraphQLSubscriptions();
    this.subscriptionGroups = new Map();
  }

  // Create a subscription group
  createGroup(groupId, subscriptions) {
    const group = {
      id: groupId,
      subscriptions: [],
      isActive: false
    };

    subscriptions.forEach(config => {
      const subscriptionId = this.subscriptions.subscribe(config, (data) => {
        this.handleGroupUpdate(groupId, data);
      });
      group.subscriptions.push(subscriptionId);
    });

    this.subscriptionGroups.set(groupId, group);
    return group;
  }

  // Activate a subscription group
  activateGroup(groupId) {
    const group = this.subscriptionGroups.get(groupId);
    if (group) {
      group.isActive = true;
      console.log(`Subscription group ${groupId} activated`);
    }
  }

  // Deactivate a subscription group
  deactivateGroup(groupId) {
    const group = this.subscriptionGroups.get(groupId);
    if (group) {
      group.isActive = false;
      console.log(`Subscription group ${groupId} deactivated`);
    }
  }

  // Handle group updates
  handleGroupUpdate(groupId, data) {
    const group = this.subscriptionGroups.get(groupId);
    if (group && group.isActive) {
      // Emit group update event
      window.dispatchEvent(new CustomEvent(`subscription-group-${groupId}`, {
        detail: data
      }));
    }
  }

  // Remove a subscription group
  removeGroup(groupId) {
    const group = this.subscriptionGroups.get(groupId);
    if (group) {
      group.subscriptions.forEach(subscriptionId => {
        this.subscriptions.unsubscribe(subscriptionId);
      });
      this.subscriptionGroups.delete(groupId);
    }
  }
}

export default graphQLSubscriptions;
